import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ReportData, Movimentacao } from '../../../pages/reports/interfaces/report-data';
import moment from 'moment';

export const PDFService = {
    exportToPDF: async (data: ReportData, reportType: string, projectName?: string): Promise<void> => {
        console.log('PDFService - data:', data);
        console.log('PDFService - reportType:', reportType);
        console.log('PDFService - projectName:', projectName);
        
        try {
            const doc = new jsPDF();
            
            // Configurações de fonte
            doc.setFont('helvetica');
            
            // Usar o nome do projeto dos dados se não for fornecido
            const nomeProject = projectName || data.name || 'Projeto';
            
            // Título do relatório
            doc.setFontSize(18);
            doc.setTextColor(74, 170, 156); // Cor #4aaa9c
            doc.text(`Relatório de ${reportType}`, 20, 25);
            
            if (nomeProject) {
                doc.setFontSize(14);
                doc.setTextColor(100, 100, 100);
                doc.text(`Projeto: ${nomeProject}`, 20, 35);
            }
            
            // Data de geração
            doc.setFontSize(10);
            doc.setTextColor(150, 150, 150);
            doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')}`, 20, 45);
            
            // Período do relatório (se disponível)
            if (data.filtros?.periodoInicio && data.filtros?.periodoFim) {
                const periodoInicio = moment(data.filtros.periodoInicio).format('DD/MM/YYYY');
                const periodoFim = moment(data.filtros.periodoFim).format('DD/MM/YYYY');
                doc.text(`Período: ${periodoInicio} a ${periodoFim}`, 20, 55);
            }
            
            let yPosition = 70;

            // Função para formatar data
            const formatDate = (date: string | Date) => {
                return moment(date).format("DD/MM/YYYY");
            };

            // Função para formatar moeda
            const formatCurrency = (value: number) => {
                return new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                }).format(value / 100);
            };
            const getBeneficiaryLabel = (item: Movimentacao) =>
                item.beneficiaryName ||
                item.nomeBeneficiario ||
                (typeof item.beneficiary === 'string' ? item.beneficiary : item.beneficiary?.name) ||
                '-';
            const movementHeaders = [['Descrição', 'Beneficiário', 'Rubrica', 'Tipo', 'Data', 'Valor']];
            
            // Determinar o tipo de dados baseado no tipo do relatório e nos dados
            const getDataType = () => {
                // Se tipoRelatorio existe no resumo, usar essa informação
                if (data.resumo?.tipoRelatorio) {
                    const tipoRelatorio = data.resumo.tipoRelatorio.toLowerCase();
                    if (tipoRelatorio.includes('rendimento') || tipoRelatorio.includes('receita')) {
                        return 'receitas';
                    }
                    if (tipoRelatorio.includes('despesa') || tipoRelatorio.includes('gasto')) {
                        return 'despesas';
                    }
                }
                
                // Verificar movimentações
                if (data?.movimentacoes && data.movimentacoes.length > 0) {
                    const tipos = data.movimentacoes.map(m => m.tipo.toLowerCase());
                    const hasReceitas = tipos.some(t => t.includes('rendimento') || t.includes('receita'));
                    const hasDespesas = tipos.some(t => t.includes('despesa') || t.includes('gasto'));
                    
                    if (hasReceitas && hasDespesas) return 'ambos';
                    if (hasReceitas) return 'receitas';
                    if (hasDespesas) return 'despesas';
                }
                
                // Verificar arrays separados
                const hasReceitas = data?.receitas && data.receitas.length > 0;
                const hasDespesas = data?.despesas && data.despesas.length > 0;
                
                if (hasReceitas && hasDespesas) return 'ambos';
                if (hasReceitas) return 'receitas';
                if (hasDespesas) return 'despesas';
                
                return 'nenhum';
            };
            
            const dataType = getDataType();
            console.log('PDFService - dataType determined:', dataType);
            
            // Tabela de movimentações
            if (data?.movimentacoes && data.movimentacoes.length > 0) {
                // Separar por tipo se necessário
                const receitas = data.movimentacoes.filter(m => 
                    m.tipo.toLowerCase().includes('rendimento') || 
                    m.tipo.toLowerCase().includes('receita')
                );
                const despesas = data.movimentacoes.filter(m => 
                    m.tipo.toLowerCase().includes('despesa') || 
                    m.tipo.toLowerCase().includes('gasto')
                );
                
                // Ordenar por data (decrescente)
                const sortData = (items: Movimentacao[]) => {
                    return [...items].sort((a, b) => {
                        const dateA = new Date(a.data).getTime();
                        const dateB = new Date(b.data).getTime();
                        return dateB - dateA;
                    });
                };
                
                if (dataType === 'despesas' || (despesas.length > 0 && receitas.length === 0)) {
                    // Apenas despesas
                    const sortedDespesas = sortData(despesas.length > 0 ? despesas : data.movimentacoes);
                    const tableData = sortedDespesas.map(item => [
                        item.descricao || 'Sem descrição',
                        getBeneficiaryLabel(item),
                        item.rubrica || '-',
                        item.tipo || '-',
                        formatDate(item.data),
                        formatCurrency(item.valor)
                    ]);
                    
                    autoTable(doc, {
                        head: movementHeaders,
                        body: tableData,
                        startY: yPosition,
                        theme: 'striped',
                        headStyles: {
                            fillColor: [211, 47, 47], // Vermelho para despesas
                            textColor: [255, 255, 255],
                            fontStyle: 'bold'
                        },
                        styles: {
                            fontSize: 8,
                            cellPadding: 3,
                        },
                        columnStyles: {
                            0: { cellWidth: 35 },
                            1: { cellWidth: 28 },
                            2: { cellWidth: 26 },
                            3: { cellWidth: 20 },
                            4: { cellWidth: 20 },
                            5: { cellWidth: 28, halign: 'right' }
                        },
                        margin: { left: 20, right: 20 }
                    });
                    
                    yPosition = (doc as any).lastAutoTable.finalY + 20;
                }
                else if (dataType === 'receitas' || (receitas.length > 0 && despesas.length === 0)) {
                    // Apenas receitas/rendimentos
                    const sortedReceitas = sortData(receitas.length > 0 ? receitas : data.movimentacoes);
                    const tableData = sortedReceitas.map(item => [
                        item.descricao || 'Sem descrição',
                        getBeneficiaryLabel(item),
                        item.rubrica || '-',
                        item.tipo || '-',
                        formatDate(item.data),
                        formatCurrency(item.valor)
                    ]);
                    
                    autoTable(doc, {
                        head: movementHeaders,
                        body: tableData,
                        startY: yPosition,
                        theme: 'striped',
                        headStyles: {
                            fillColor: [46, 125, 50], // Verde para receitas
                            textColor: [255, 255, 255],
                            fontStyle: 'bold'
                        },
                        styles: {
                            fontSize: 8,
                            cellPadding: 3,
                        },
                        columnStyles: {
                            0: { cellWidth: 35 },
                            1: { cellWidth: 28 },
                            2: { cellWidth: 26 },
                            3: { cellWidth: 20 },
                            4: { cellWidth: 20 },
                            5: { cellWidth: 28, halign: 'right' }
                        },
                        margin: { left: 20, right: 20 }
                    });
                    
                    yPosition = (doc as any).lastAutoTable.finalY + 20;
                }
                else if (dataType === 'ambos') {
                    // Seção de Receitas
                    if (receitas.length > 0) {
                        doc.setFontSize(14);
                        doc.setTextColor(46, 125, 50); // Verde
                        doc.text('Receitas/Rendimentos', 20, yPosition);
                        yPosition += 10;
                        
                        const sortedReceitas = sortData(receitas);
                        const receitasData = sortedReceitas.map(item => [
                            item.descricao || 'Sem descrição',
                            getBeneficiaryLabel(item),
                            item.rubrica || '-',
                            item.tipo || '-',
                            formatDate(item.data),
                            formatCurrency(item.valor)
                        ]);
                        
                        autoTable(doc, {
                            head: movementHeaders,
                            body: receitasData,
                            startY: yPosition,
                            theme: 'striped',
                            headStyles: {
                                fillColor: [46, 125, 50], // Verde
                                textColor: [255, 255, 255],
                                fontStyle: 'bold'
                            },
                            styles: {
                                fontSize: 8,
                                cellPadding: 3,
                            },
                            columnStyles: {
                                0: { cellWidth: 35 },
                                1: { cellWidth: 28 },
                                2: { cellWidth: 26 },
                                3: { cellWidth: 20 },
                                4: { cellWidth: 20 },
                                5: { cellWidth: 28, halign: 'right' }
                            },
                            margin: { left: 20, right: 20 }
                        });
                        
                        yPosition = (doc as any).lastAutoTable.finalY + 20;
                    }
                    
                    // Seção de Despesas
                    if (despesas.length > 0) {
                        if (yPosition > 200) {
                            doc.addPage();
                            yPosition = 20;
                        }
                        
                        doc.setFontSize(14);
                        doc.setTextColor(211, 47, 47); // Vermelho
                        doc.text('Despesas', 20, yPosition);
                        yPosition += 10;
                        
                        const sortedDespesas = sortData(despesas);
                        const despesasData = sortedDespesas.map(item => [
                            item.descricao || 'Sem descrição',
                            getBeneficiaryLabel(item),
                            item.rubrica || '-',
                            item.tipo || '-',
                            formatDate(item.data),
                            formatCurrency(item.valor)
                        ]);
                        
                        autoTable(doc, {
                            head: movementHeaders,
                            body: despesasData,
                            startY: yPosition,
                            theme: 'striped',
                            headStyles: {
                                fillColor: [211, 47, 47], // Vermelho
                                textColor: [255, 255, 255],
                                fontStyle: 'bold'
                            },
                            styles: {
                                fontSize: 8,
                                cellPadding: 3,
                            },
                            columnStyles: {
                                0: { cellWidth: 35 },
                                1: { cellWidth: 28 },
                                2: { cellWidth: 26 },
                                3: { cellWidth: 20 },
                                4: { cellWidth: 20 },
                                5: { cellWidth: 28, halign: 'right' }
                            },
                            margin: { left: 20, right: 20 }
                        });
                        
                        yPosition = (doc as any).lastAutoTable.finalY + 20;
                    }
                }
            }
            // Se vierem arrays separados (compatibilidade)
            else if (data?.receitas || data?.despesas) {
                // Processar arrays separados (mesma lógica adaptada)
                if (data.receitas && data.receitas.length > 0) {
                    const tableData = data.receitas.map(item => [
                        item.descricao || 'Sem descrição',
                        getBeneficiaryLabel(item),
                        item.rubrica || '-',
                        item.tipo || '-',
                        formatDate(item.data),
                        formatCurrency(item.valor)
                    ]);
                    
                    autoTable(doc, {
                        head: movementHeaders,
                        body: tableData,
                        startY: yPosition,
                        theme: 'striped',
                        headStyles: {
                            fillColor: [46, 125, 50],
                            textColor: [255, 255, 255],
                            fontStyle: 'bold'
                        },
                        styles: {
                            fontSize: 8,
                            cellPadding: 3,
                        },
                        columnStyles: {
                            0: { cellWidth: 35 },
                            1: { cellWidth: 28 },
                            2: { cellWidth: 26 },
                            3: { cellWidth: 20 },
                            4: { cellWidth: 20 },
                            5: { cellWidth: 28, halign: 'right' }
                        },
                        margin: { left: 20, right: 20 }
                    });
                    
                    yPosition = (doc as any).lastAutoTable.finalY + 20;
                }
                
                if (data.despesas && data.despesas.length > 0) {
                    const tableData = data.despesas.map(item => [
                        item.descricao || 'Sem descrição',
                        getBeneficiaryLabel(item),
                        item.rubrica || '-',
                        item.tipo || '-',
                        formatDate(item.data),
                        formatCurrency(item.valor)
                    ]);
                    
                    autoTable(doc, {
                        head: movementHeaders,
                        body: tableData,
                        startY: yPosition,
                        theme: 'striped',
                        headStyles: {
                            fillColor: [211, 47, 47],
                            textColor: [255, 255, 255],
                            fontStyle: 'bold'
                        },
                        styles: {
                            fontSize: 8,
                            cellPadding: 3,
                        },
                        columnStyles: {
                            0: { cellWidth: 35 },
                            1: { cellWidth: 28 },
                            2: { cellWidth: 26 },
                            3: { cellWidth: 20 },
                            4: { cellWidth: 20 },
                            5: { cellWidth: 28, halign: 'right' }
                        },
                        margin: { left: 20, right: 20 }
                    });
                    
                    yPosition = (doc as any).lastAutoTable.finalY + 20;
                }
            }
            
            // Resumo financeiro
            if (data?.resumo) {
                if (yPosition > 200) {
                    doc.addPage();
                    yPosition = 20;
                }
                
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(14);
                doc.setTextColor(74, 170, 156);
                doc.text('Resumo Financeiro', 20, yPosition);
                
                yPosition += 15;
                
                const resumoData: any[] = [];
                
                // Valor do Projeto (se disponível)
                if (data.resumo.valorProjeto) {
                    resumoData.push(['Valor do Projeto', formatCurrency(data.resumo.valorProjeto)]);
                }
                
                // Mostrar totais baseado no tipo de dados
                if (dataType === 'despesas') {
                    if (data.resumo.totalDespesas !== undefined) {
                        resumoData.push(['Total de Despesas', formatCurrency(data.resumo.totalDespesas)]);
                    }
                    if (data.resumo.saldo !== undefined) {
                        resumoData.push(['Saldo Disponível', formatCurrency(data.resumo.saldo)]);
                    }
                }
                else if (dataType === 'receitas') {
                    if (data.resumo.totalReceitas !== undefined) {
                        resumoData.push(['Total de Receitas', formatCurrency(data.resumo.totalReceitas)]);
                    }
                    if (data.resumo.saldo !== undefined) {
                        resumoData.push(['Total Arrecadado', formatCurrency(data.resumo.saldo)]);
                    }
                }
                else if (dataType === 'ambos') {
                    if (data.resumo.totalReceitas !== undefined) {
                        resumoData.push(['Total de Receitas', formatCurrency(data.resumo.totalReceitas)]);
                    }
                    if (data.resumo.totalDespesas !== undefined) {
                        resumoData.push(['Total de Despesas', formatCurrency(data.resumo.totalDespesas)]);
                    }
                    if (data.resumo.saldo !== undefined) {
                        resumoData.push(['Saldo Líquido', formatCurrency(data.resumo.saldo)]);
                    }
                }
                
                // Percentual de execução (se disponível)
                if (data.resumo.percentualExecucao !== undefined) {
                    resumoData.push(['Percentual de Execução', `${data.resumo.percentualExecucao.toFixed(2)}%`]);
                }
                
                if (resumoData.length > 0) {
                    autoTable(doc, {
                        body: resumoData,
                        startY: yPosition,
                        theme: 'plain',
                        styles: {
                            fontSize: 11,
                            cellPadding: 5,
                        },
                        columnStyles: {
                            0: { 
                                cellWidth: 80,
                                fontStyle: 'bold'
                            },
                            1: { 
                                cellWidth: 60,
                                halign: 'right'
                            }
                        },
                        didParseCell: function(cellData) {
                            if (cellData.column.index === 1 && cellData.cell.section === 'body') {
                                const rowIndex = cellData.row.index;
                                const firstColumnText = resumoData[rowIndex][0];
                                
                                if (firstColumnText === 'Total de Receitas' || firstColumnText === 'Total Arrecadado') {
                                    cellData.cell.styles.textColor = [46, 125, 50]; // Verde
                                } else if (firstColumnText === 'Total de Despesas') {
                                    cellData.cell.styles.textColor = [211, 47, 47]; // Vermelho
                                } else if (firstColumnText === 'Saldo Disponível' || firstColumnText === 'Saldo Líquido') {
                                    const saldoText = cellData.cell.text[0] || '';
                                    if (saldoText.includes('-')) {
                                        cellData.cell.styles.textColor = [211, 47, 47]; // Vermelho
                                    } else {
                                        cellData.cell.styles.textColor = [46, 125, 50]; // Verde
                                    }
                                    cellData.cell.styles.fontStyle = 'bold';
                                } else if (firstColumnText === 'Percentual de Execução') {
                                    cellData.cell.styles.textColor = [74, 170, 156]; // Cor tema
                                }
                            }
                        },
                        margin: { left: 20, right: 20 }
                    });
                }
            }
            
            // Rodapé
            const pageCount = doc.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(150, 150, 150);
                doc.text(`Página ${i} de ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
                doc.text('Gerado pelo Sistema Gestore', 20, doc.internal.pageSize.height - 10);
            }
            
            // Nome do arquivo
            const fileName = `${nomeProject}_${reportType}_${new Date().toISOString().slice(0, 10)}.pdf`;
            
            // Download do arquivo
            doc.save(fileName);
            
        } catch (error) {
            console.error('Erro ao gerar relatório PDF:', error);
            throw new Error('Erro ao gerar relatório PDF: ' + error);
        }
    }
};

export default PDFService;
